/**
 * wysiwyg-visual-tools/registry.ts
 *
 * VtbRegistry — orquestra todos os plugins do Smart Visual Editor.
 *
 * Fluxo:
 *   1. O modal instancia um VtbRegistry e o conecta ao DOM (setRefs)
 *   2. Registra cada plugin (register)
 *   3. Chama init() → instala mousedown no visualEditor + chama setup() de cada plugin
 *   4. A cada clique, handleClick() percorre os plugins por prioridade e ativa o mais específico
 */

import { VtbPlugin, IVtbRegistry, SmartCtx } from './types';

interface VtbModalRefs {
    modalEl: HTMLElement | null;
    visualEditor: HTMLElement | null;
    htmlEditor: HTMLTextAreaElement | null;
    previewEl: HTMLElement | null;
    miniModalEl: HTMLElement | null;
}

/** Retorna a profundidade de um elemento na árvore DOM. */
function domDepth(el: HTMLElement): number {
    let depth = 0;
    let cur: HTMLElement | null = el;
    while (cur) { depth++; cur = cur.parentElement; }
    return depth;
}

/** Posição de inserção do mini modal */
type InsertPosition = 'before' | 'inside' | 'after';

export class VtbRegistry implements IVtbRegistry {
    // ── Refs do DOM ───────────────────────────────────────────────────────────
    private _modalEl: HTMLElement | null = null;
    private _visualEditor: HTMLElement | null = null;
    private _htmlEditor: HTMLTextAreaElement | null = null;
    private _previewEl: HTMLElement | null = null;
    private _miniModalEl: HTMLElement | null = null;

    private dynamicSelectors: Set<string> = new Set();
    private categories: any[] = [];

    // ── Estado interno ────────────────────────────────────────────────────────
    private plugins: VtbPlugin[] = [];
    private _activeCtx: SmartCtx | null = null;
    private _contextStack: SmartCtx[] = [];

    /** Range salvo antes de abrir o mini modal (para restaurar cursor no insert). */
    private _savedRange: Range | null = null;

    /** Posição de inserção selecionada no mini modal. */
    private _insertPos: InsertPosition = 'inside';

    // ── Getters ───────────────────────────────────────────────────────────────
    get modalEl() { return this._modalEl; }
    get visualEditor() { return this._visualEditor; }
    get htmlEditor() { return this._htmlEditor; }
    get previewEl() { return this._previewEl; }
    get activeCtx() { return this._activeCtx; }
    get contextStack() { return this._contextStack; }

    // ─────────────────────────────────────────────────────────────────────────
    // CONFIGURAÇÃO
    // ─────────────────────────────────────────────────────────────────────────

    setRefs(refs: VtbModalRefs): void {
        this._modalEl = refs.modalEl;
        this._visualEditor = refs.visualEditor;
        this._htmlEditor = refs.htmlEditor;
        this._previewEl = refs.previewEl;
        this._miniModalEl = refs.miniModalEl;
    }

    addComponentSelectors(tags: string[]): void {
        tags.forEach(tag => this.dynamicSelectors.add(tag));
    }

    setCategories(categories: any[]): void {
        this.categories = categories;
    }

    register(plugin: VtbPlugin): void {
        this.plugins.push(plugin);
    }

    init(): void {
        if (!this._visualEditor) return;

        this._visualEditor.addEventListener('mousedown', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target || target === this._visualEditor) {
                this.clearSelection();
                return;
            }
            setTimeout(() => this.handleClick(target), 0);
        });

        // Rastreia continuamente o cursor dentro do editor.
        // selectionchange dispara a cada movimento de cursor/seleção,
        // garantindo que _savedRange sempre reflete a posição real.
        document.addEventListener('selectionchange', () => {
            if (!this._visualEditor) return;
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                if (this._visualEditor.contains(range.commonAncestorContainer)) {
                    this._savedRange = range.cloneRange();
                }
            }
        });

        for (const plugin of this.plugins) {
            plugin.setup(this);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // DETECÇÃO + ATIVAÇÃO
    // ─────────────────────────────────────────────────────────────────────────

    handleClick(target: HTMLElement): void {
        if (!this._visualEditor) return;

        this._visualEditor.querySelectorAll('.vtb-selected')
            .forEach(e => e.classList.remove('vtb-selected'));

        const newStack: SmartCtx[] = [];

        for (const plugin of this.plugins) {
            const ctx = plugin.detect(target, this._visualEditor);
            if (ctx) newStack.push(ctx);
        }

        if (newStack.length > 0) {
            // Ordena do mais específico (maior profundidade DOM) para o mais genérico
            newStack.sort((a, b) => domDepth(b.el) - domDepth(a.el));
            this._contextStack = newStack;
            this.switchToContext(0);
            return;
        }

        this._contextStack = [];
        this._activeCtx = null;
        this._showHint();
        this._renderBreadcrumbs();
    }

    switchToContext(index: number): void {
        const ctx = this._contextStack[index];
        if (!ctx) return;

        this._activeCtx = ctx;

        this._visualEditor?.querySelectorAll('.vtb-selected')
            .forEach(e => e.classList.remove('vtb-selected'));
        ctx.el.classList.add('vtb-selected');


        this.hideAllPanels();
        const hint = this.q('vtb-hint');
        if (hint) hint.style.display = 'none';

        const plugin = this.plugins.find(p => p.panelId === ctx.type);
        if (plugin) {
            this.showPanel(plugin.panelId);
            plugin.activate(ctx, this);
        }

        this._renderBreadcrumbs();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // INTERFACE IVtbRegistry
    // ─────────────────────────────────────────────────────────────────────────

    q<T extends HTMLElement = HTMLElement>(id: string): T | null {
        if (!this._modalEl) return null;

        const selector = id.startsWith('#') ? id : '#' + id;
        return this._modalEl.querySelector<T>(selector);
    }

    sync(): void {
        if (!this._htmlEditor || !this._visualEditor) return;
        this._htmlEditor.value = this._visualEditor.innerHTML;
        if (this._previewEl) this._previewEl.innerHTML = this._visualEditor.innerHTML;
    }

    selectEl(el: HTMLElement): void {
        this.handleClick(el);
    }

    clearSelection(): void {
        this._visualEditor?.querySelectorAll('.vtb-selected')
            .forEach(e => e.classList.remove('vtb-selected'));
        this._contextStack = [];
        this._activeCtx = null;
        this._savedRange = null;
        this._showHint();
        this._renderBreadcrumbs();
    }

    showPanel(id: string): void {
        const g = this.q<HTMLElement>(id);
        if (g) g.style.display = 'flex';
    }

    /**
     * Descobre filhos editáveis do elemento pai.
     * Usa selectorHints de cada plugin + seletores dinâmicos registrados.
     */
    getChildrenContexts(parentEl: HTMLElement): SmartCtx[] {
        const contexts: SmartCtx[] = [];

        // Monta seletor unificado a partir dos selectorHints de cada plugin
        const hints: string[] = [];
        for (const plugin of this.plugins) {
            if (plugin.selectorHints) {
                hints.push(...plugin.selectorHints);
            }
        }

        // Complementa com seletores dinâmicos registrados externamente
        this.dynamicSelectors.forEach(tag => {
            hints.push(`.${tag.replace(/_/g, '-')}`, `.${tag.replace(/-/g, '_')}`);
        });

        if (hints.length === 0) return contexts;

        const finalSelector = [...new Set(hints)].join(', ');

        let candidates: HTMLElement[];
        try {
            candidates = Array.from(parentEl.querySelectorAll<HTMLElement>(finalSelector));
        } catch {
            return contexts;
        }

        candidates.forEach(el => {
            if (el === parentEl) return;
            for (const plugin of this.plugins) {
                const ctx = plugin.detect(el, parentEl);
                if (ctx) {
                    this.ensureVtbId(el);
                    contexts.push(ctx);
                    break;
                }
            }
        });

        return contexts;
    }

    private ensureVtbId(el: HTMLElement): void {
        if (!el.hasAttribute('data-vtb-id')) {
            const tag = el.tagName.toLowerCase();
            const random = Math.random().toString(36).substring(2, 7);
            el.setAttribute('data-vtb-id', `${tag}-${random}`);
        }
    }

    hideAllPanels(): void {
        this._modalEl?.querySelectorAll<HTMLElement>('.vtb-group')
            .forEach(p => p.style.display = 'none');
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CURSOR / RANGE
    // ─────────────────────────────────────────────────────────────────────────

    private _saveRange(): void {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && this._visualEditor) {
            const range = sel.getRangeAt(0);
            if (this._visualEditor.contains(range.commonAncestorContainer)) {
                this._savedRange = range.cloneRange();
                return;
            }
        }
        // Mantém o range anterior se não houver seleção nova no editor
    }

    private _restoreRange(): void {
        if (!this._savedRange) return;
        try {
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(this._savedRange);
            }
        } catch {
            // Range pode ter se tornado inválido
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PRIVADO — UI
    // ─────────────────────────────────────────────────────────────────────────

    private _showHint(): void {
        this.hideAllPanels();
        const hint = this.q<HTMLElement>('vtb-hint');
        if (hint) hint.style.display = '';
    }

    private _renderBreadcrumbs(): void {
        const container = this.q('vtb-breadcrumbs');
        if (!container) return;

        if (this._contextStack.length === 0 || !this._activeCtx) {
            container.innerHTML = `<div class="vtb-breadcrumb-item active">Selecione um componente</div>`;
            container.style.display = 'flex';
            return;
        }

        const children = this.getChildrenContexts(this._activeCtx.el);

        container.style.display = 'flex';

        // contextStack já está ordenado: [0]=mais específico ... [n]=mais genérico
        // No breadcrumb mostramos do mais genérico → mais específico (esquerda → direita)
        const breadcrumbList = [...this._contextStack].reverse();

        container.innerHTML = breadcrumbList.map((ctx) => {
            // O item ativo é aquele que corresponde ao _activeCtx
            const isActive = ctx === this._activeCtx;
            const originalIdx = this._contextStack.indexOf(ctx);
            return `<div class="vtb-breadcrumb-item${isActive ? ' active' : ''}" data-idx="${originalIdx}">${ctx.label}</div>`;
        }).join('<div class="vtb-breadcrumb-sep">›</div>');

        // Botão "🧩 Explorar filhos"
        if (children.length > 0) {
            const sep = document.createElement('div');
            sep.className = 'vtb-breadcrumb-sep';
            sep.textContent = '›';
            container.appendChild(sep);

            const btn = document.createElement('div');
            btn.className = 'vtb-breadcrumb-item vtb-explore-toggle';
            btn.innerHTML = `🧩 Explorar (${children.length})`;
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._showChildrenMenu(btn, children);
            });
            container.appendChild(btn);
        }

        // Botão "➕ Inserir"
        // mousedown: preventDefault impede que o browser mova o foco para fora
        // do contenteditable, preservando o cursor exato para o insert "Dentro".
        const insertBtn = document.createElement('div');
        insertBtn.className = 'vtb-breadcrumb-item vtb-insert-btn';
        insertBtn.innerHTML = '➕ Inserir';
        insertBtn.addEventListener('mousedown', (e) => { e.preventDefault(); });
        insertBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openMiniModal(insertBtn);
        });
        container.appendChild(insertBtn);

        // Listeners dos items de breadcrumb
        container.querySelectorAll<HTMLElement>('.vtb-breadcrumb-item[data-idx]').forEach(el => {
            el.addEventListener('click', () => {
                const idx = parseInt(el.dataset.idx ?? '0');
                this.switchToContext(idx);
            });
        });
    }

    private _showChildrenMenu(anchor: HTMLElement, children: SmartCtx[]): void {
        document.querySelectorAll('.vtb-dropdown-menu').forEach(m => m.remove());
        const menu = document.createElement('div');
        menu.className = 'vtb-dropdown-menu';

        children.forEach(child => {
            const item = document.createElement('div');
            item.className = 'vtb-dropdown-item';
            const preview = (child.el.innerText || '').substring(0, 24).trim();
            item.innerHTML = `<strong>${child.label}</strong>${preview ? ` <span style="opacity:0.6;font-size:10px">${preview}…</span>` : ''}`;
            item.addEventListener('click', () => {
                this.selectEl(child.el);
                menu.remove();
            });
            menu.appendChild(item);
        });

        menu.style.position = 'fixed';
        document.body.appendChild(menu);
        this._positionFloating(menu, anchor);

        const closer = (e: MouseEvent) => {
            if (!menu.contains(e.target as Node) && e.target !== anchor) {
                menu.remove();
                document.removeEventListener('mousedown', closer);
            }
        };
        document.addEventListener('mousedown', closer);
    }

    // ── MINI MODAL ────────────────────────────────────────────────────────────

    openMiniModal(anchor: HTMLElement): void {
        const modal = this._miniModalEl;
        if (!modal) return;

        // Recria o nó interno para limpar todos os listeners acumulados
        const oldContent = modal.querySelector('.vtb-mini-content');
        if (oldContent) {
            const fresh = oldContent.cloneNode(false) as HTMLElement;
            oldContent.replaceWith(fresh);
        }

        modal.style.display = 'flex';
        this._positionFloating(modal, anchor, 340);

        this._renderMiniPositionSelector(modal);
        this._renderMiniCategories();

        // Botão fechar — usa removeEventListener para garantir sem duplicatas
        const closeBtn = modal.querySelector('#vtb-mini-modal-close');
        if (closeBtn) {
            const freshClose = closeBtn.cloneNode(true) as HTMLElement;
            closeBtn.replaceWith(freshClose);
            freshClose.addEventListener('mousedown', (e) => { e.preventDefault(); });
            freshClose.addEventListener('click', () => { modal.style.display = 'none'; });
        }
    }

    /**
     * Renderiza seletor de posição de inserção (Antes / Dentro / Depois).
     * Exibido no topo do mini modal.
     */
    private _renderMiniPositionSelector(modal: HTMLElement): void {
        let posBar = modal.querySelector<HTMLElement>('.vtb-insert-pos-bar');
        if (!posBar) {
            posBar = document.createElement('div');
            posBar.className = 'vtb-insert-pos-bar';
            modal.insertBefore(posBar, modal.querySelector('.vtb-mini-content'));
        }
        posBar.innerHTML = '';

        const positions: { key: InsertPosition; label: string }[] = [
            { key: 'before', label: '⬆ Antes' },
            { key: 'inside', label: '↳ Dentro' },
            { key: 'after', label: '⬇ Depois' },
        ];

        positions.forEach(({ key, label }) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = label;
            btn.className = 'vtb-btn vtb-btn-sm' + (this._insertPos === key ? ' vtb-btn-active' : '');
            btn.style.cssText = 'flex:1;font-size:11px;';
            btn.addEventListener('mousedown', (e) => { e.preventDefault(); });
            btn.addEventListener('click', () => {
                this._insertPos = key;
                this._renderMiniPositionSelector(modal);
            });
            posBar!.appendChild(btn);
        });
    }

    private _renderMiniCategories(): void {
        const content = this._miniModalEl?.querySelector('.vtb-mini-content');
        if (!content) return;
        content.innerHTML = '';

        const activeType = this._activeCtx?.type ?? '';

        // Ordena categorias: afinidade com o contexto atual primeiro
        const sorted = [...this.categories].sort((a, b) => {
            const aAffinity = this._categoryHasAffinity(a, activeType) ? 0 : 1;
            const bAffinity = this._categoryHasAffinity(b, activeType) ? 0 : 1;
            return aAffinity - bAffinity;
        });

        sorted.forEach(cat => {
            const isAffinite = this._categoryHasAffinity(cat, activeType);
            const div = document.createElement('div');
            div.className = 'vtb-mini-category' + (isAffinite ? ' vtb-mini-category-highlight' : '');
            div.innerHTML = `<span>${cat.label || cat.tag}</span><span style="display:flex;align-items:center;gap:4px">${isAffinite ? '<span style="font-size:9px;opacity:0.7">★</span>' : ''}<span>›</span></span>`;
            div.addEventListener('click', () => this._renderMiniExamples(cat));
            content.appendChild(div);
        });
    }

    /**
     * Verifica se uma categoria tem afinidade com o tipo de contexto ativo.
     * Uma categoria tem afinidade se o plugin que a gerencia declara `contextAffinity`
     * contendo o panelId ativo, ou se o tag da categoria bate com o panelId ativo.
     */
    private _categoryHasAffinity(category: any, activeType: string): boolean {
        if (!activeType) return false;

        // Verifica pelo campo affinity da própria categoria (se o backend enviar)
        if (Array.isArray(category.affinity) && category.affinity.includes(activeType)) {
            return true;
        }

        // Verifica via selectorHints/contextAffinity dos plugins registrados
        for (const plugin of this.plugins) {
            if (plugin.contextAffinity && plugin.contextAffinity.includes(activeType)) {
                // Se o tag da categoria bate com algo que este plugin representa
                if (category.tag && plugin.selectorHints) {
                    const tagLower = category.tag.toLowerCase().replace(/[^a-z-]/g, '');
                    const matches = plugin.selectorHints.some(h => h.includes(tagLower));
                    if (matches) return true;
                }
            }
        }

        return false;
    }

    /**
     * Renderiza os componentes de uma categoria.
     * Estrutura da API:
     *   ComponentCategory { tag, label, components: ComponentItem[] }
     *   ComponentItem     { hash, tag, description, examples: Record<string,string>, preview_html }
     */
    private _renderMiniExamples(category: any): void {
        const content = this._miniModalEl?.querySelector('.vtb-mini-content');
        if (!content) return;

        content.innerHTML = `
            <div style="padding:4px 4px 8px;display:flex;gap:8px;align-items:center;border-bottom:1px solid var(--vtb-border);margin-bottom:6px;">
                <button type="button" class="vtb-btn vtb-btn-icon" id="vtb-mini-back" style="padding:0;width:24px;height:24px;flex-shrink:0;">←</button>
                <small style="opacity:0.7">Componentes: <strong>${category.label || category.tag}</strong></small>
            </div>
        `;

        const backBtn = content.querySelector('#vtb-mini-back');
        backBtn?.addEventListener('mousedown', (e) => { e.preventDefault(); });
        backBtn?.addEventListener('click', () => this._renderMiniCategories());

        // Estrutura correta: category.components é ComponentItem[]
        const components: any[] = Array.isArray(category.components) ? category.components : [];

        if (components.length === 0) {
            const empty = document.createElement('div');
            empty.style.cssText = 'padding:20px;text-align:center;opacity:0.5;font-size:12px;';
            empty.textContent = 'Nenhum componente nesta categoria.';
            content.appendChild(empty);
            return;
        }

        components.forEach((item: any) => {
            const div = document.createElement('div');
            div.className = 'vtb-mini-example';
            div.title = item.description || item.tag || '';

            const previewHtml = item.preview_html || '';

            // HTML de inserção: primeiro exemplo disponível, senão preview_html
            const examples: Record<string, string> = item.examples || {};
            const exampleKeys = Object.keys(examples);
            const insertHtml = exampleKeys.length > 0 ? examples[exampleKeys[0]] : previewHtml;

            // Humaniza a tag para exibir como nome
            const humanTag = (item.tag || '').split('_')
                .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
                .join(' ');

            div.innerHTML = `
                <div style="padding:10px 12px">
                    <div style="font-size:12px; font-weight:700; color:var(--vtb-primary); margin-bottom:2px;">${humanTag}</div>
                    ${item.description ? `<div style="font-size:11px; opacity:0.7; line-height:1.4;">${item.description}</div>` : ''}
                </div>
            `;

            // preventDefault no mousedown preserva o foco do contenteditable
            // e garante que _savedRange ainda é válido no momento do insert.
            div.addEventListener('mousedown', (e) => { e.preventDefault(); });
            div.addEventListener('click', () => {
                if (insertHtml) {
                    this._insertHtml(insertHtml);
                    if (this._miniModalEl) this._miniModalEl.style.display = 'none';
                }
            });
            content.appendChild(div);
        });
    }

    /**
     * Insere HTML respeitando a posição selecionada (before / inside / after)
     * e tentando restaurar o cursor salvo para insert do tipo "inside".
     */
    private _insertHtml(html: string): void {
        if (!this._activeCtx) return;

        const activeEl = this._activeCtx.el;
        const fragment = document.createRange().createContextualFragment(html);

        if (this._insertPos === 'before') {
            activeEl.parentElement?.insertBefore(fragment, activeEl);
            this.sync();
            return;
        }

        if (this._insertPos === 'after') {
            activeEl.parentElement?.insertBefore(fragment, activeEl.nextSibling);
            this.sync();
            return;
        }

        // Posição "inside" — tenta usar o cursor salvo primeiro
        if (this._savedRange && this._visualEditor) {
            try {
                this._restoreRange();
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                    const range = sel.getRangeAt(0);
                    if (activeEl.contains(range.commonAncestorContainer)) {
                        const frag2 = range.createContextualFragment(html);
                        range.deleteContents();
                        range.insertNode(frag2);
                        this.sync();
                        return;
                    }
                }
            } catch {
                // Fallback para append
            }
        }

        // Fallback: append no final do elemento ativo
        activeEl.appendChild(document.createRange().createContextualFragment(html));
        this.sync();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // UTILITÁRIO — posicionamento de floatings
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Posiciona um elemento flutuante abaixo (ou acima) de um anchor,
     * garantindo que não saia do viewport.
     */
    /**
     * Posiciona um elemento com position:fixed abaixo (ou acima) do anchor,
     * garantindo que não saia do viewport.
     */
    private _positionFloating(el: HTMLElement, anchor: HTMLElement, minWidth = 0): void {
        const prevVis = el.style.visibility;
        el.style.visibility = 'hidden';
        el.style.display = 'flex';

        const rect = anchor.getBoundingClientRect();
        const elW = Math.max(el.offsetWidth, minWidth);
        const elH = el.offsetHeight;

        // Horizontal — alinha à esquerda, corrige overflow
        let left = rect.left;
        if (left + elW > window.innerWidth - 8) left = window.innerWidth - elW - 8;
        left = Math.max(8, left);

        // Vertical — abaixo se couber, acima caso contrário
        const spaceBelow = window.innerHeight - rect.bottom;
        let top: number;
        if (spaceBelow >= elH + 8 || spaceBelow >= 120) {
            top = rect.bottom + 4;
        } else {
            top = rect.top - elH - 4;
        }
        top = Math.max(8, top);

        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.minWidth = minWidth ? `${minWidth}px` : '';
        el.style.visibility = prevVis;
    }
}
